import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { combineLatest, iif, Observable, of, Subject } from 'rxjs';
import {
  combineLatestWith,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';

import {
  ConsolidatedFeature,
  DcTypes,
  DeviceNameCommands,
  DeviceNameProperties,
  DeviceRoles,
  EmsKpiProperties,
  EmsPowerBalanceProperties,
  EmsPowerBalanceProperty,
  EmsPowerBalanceTypes,
  EmsPowerFlowsProperties,
  Feature,
  FeatureNames,
  FeaturePowerLimitationOverview,
  FeaturePropertyKey,
  HeatingRodHeatingCommands,
  HeatingRodHeatingProperties,
  HeatingRodStatusProperties,
  HemsConfigurationOverviewProperties,
  HemsType,
  IsNumberValueProperty,
  NumberValue,
  PhotovoltaicStatusProperties,
  PowerLimitationType,
  ThermalCockpitProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  DevicesStore,
  DeviceTypes,
  GatewayStore,
  Helpers,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RichDevice,
} from '@store';

import { HemsLiteInfoDialogComponent } from './hems-lite-info-dialog/hems-lite-info-dialog.component';
import { HemsFlowType } from '../../../@store/energy-historical-data/energy-historical-data.helpers';
import {
  EnergyFlowDirections,
  EnergyFlowInType,
  EnergyFlowOutType,
} from '../../../operational-data-common/translation-pipes/translate-energy-flow-type.pipe';
import { TranslateService } from '@ngx-translate/core';
import { DevicesService } from 'src/app/@store/devices/devices.service';
import { ElectricHeaterService } from '../electric-heater/electric-heater.service';

@Component({
  selector: 'app-energy-flow',
  templateUrl: './energy-flow.component.html',
  styleUrls: ['./energy-flow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyFlowComponent implements OnInit, OnDestroy {
  readonly FLOW_TYPES = HemsFlowType;
  readonly ENERGY_FLOW_IN_TYPE = EnergyFlowInType;
  readonly ENERGY_FLOW_OUT_TYPE = EnergyFlowOutType;
  readonly ENERGY_FLOW_DIRECTIONS = EnergyFlowDirections;
  readonly emsPowerBalanceTypes = EmsPowerBalanceTypes;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly possibleRunningRodStatusValues = ['1', '2', '3'];
  readonly PowerLimitationType = PowerLimitationType;
  readonly hemsType = HemsType;

  stateOfCharge$: Observable<Feature<IsNumberValueProperty, never>>;
  stateOfChargeConsolidated$: Observable<Feature<ConsolidatedFeature, never>>;
  indicators$: Observable<Feature<EmsKpiProperties, never>>;
  powerBalance: Feature<EmsPowerBalanceProperties, never>;
  powerFlows$: Observable<Feature<EmsPowerFlowsProperties, never>>;
  pvStatus$: Observable<Feature<PhotovoltaicStatusProperties, never>>;

  heatingRodNotificationVisible$: Observable<boolean>;
  heatingSpfTotal$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingRodHeating$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;
  heatingRodDhw$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;
  dryConcatPowerInstantaneousPower$: Observable<Feature<IsNumberValueProperty, never>>;
  emsPowerBalance$: Observable<Feature<EmsPowerBalanceProperties, never>>;
  powerBalanceTypeToValueMap = new Map<EmsPowerBalanceTypes, EmsPowerBalanceProperty[]>();
  dryContactgenericDevicePowerDetails: EmsPowerBalanceProperty[] = [];
  genericDeviceNames$: Observable<{ id: string; name: string }[]>;
  powerBalanceOrder = [
    EmsPowerBalanceTypes.Household,
    EmsPowerBalanceTypes.Heatpump,
    EmsPowerBalanceTypes.dryContactHeatpump,
    EmsPowerBalanceTypes.Accumulator,
    EmsPowerBalanceTypes.VCS,
    EmsPowerBalanceTypes.PhotovoltaicsHeater,
    EmsPowerBalanceTypes.dryContactGeneric,
    EmsPowerBalanceTypes.Grid,
  ];

  thermalCockpit$: Observable<Feature<ThermalCockpitProperties, never>>;
  consumptionLimitation$: Observable<FeaturePowerLimitationOverview>;
  productionLimitation$: Observable<FeaturePowerLimitationOverview>;
  activeHemsType$: Observable<Feature<HemsConfigurationOverviewProperties, never>>;

  isThermalFlow = false;
  isOBNG = false;
  wallboxNamesWithBusAddress: { name: string; busAddress: string }[] = [];

  private readonly otherDeviceFallbackNameTranslationKey =
    '+INSTALLATIONS.DRY_CONTACTS.GENERIC_CONSUMER.OTHER_DEVICE';
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    private gtmService: GTMService,
    private cdr: ChangeDetectorRef,
    private installationFeaturesStore: InstallationFeaturesStore,
    private installationDetailsStore: InstallationDetailsStore,
    private translate: TranslateService,
    private dialog: MatDialog,
    private devicesService: DevicesService,
    private electriHeaterService: ElectricHeaterService
  ) {}

  ngOnInit(): void {
    this.setWallboxNames();
    this.thermalCockpit$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.thermalCockpit
    );

    this.indicators$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.emsKeyPerformanceIndicators
    );
    this.heatingSpfTotal$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingSpfTotal,
      undefined,
      DeviceRoles.SystemController
    );
    this.heatingRodHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingRodHeating
    );
    this.heatingRodDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingRodDhw
    );
    this.heatingRodNotificationVisible$ = this.electriHeaterService
      .aggregatedHeatingRodsIndexesWithBoilerSerials()
      .pipe(
        switchMap((data) => {
          const features = data.map((el) =>
            this.installationFeaturesStore.selectInstallationDeviceFeature<
              HeatingRodStatusProperties,
              never
            >(FeatureNames.heatingRodsHeatGenerationStatus(el.heatingRodIndex))
          );

          return combineLatest(features);
        }),
        map((features) =>
          features.some((feature) =>
            this.possibleRunningRodStatusValues.includes(Helpers.getHeatingRodStatusValue(feature))
          )
        )
      );
    this.activeHemsType$ = this.installationFeaturesStore.selectInstallationGatewayFeature<
      HemsConfigurationOverviewProperties,
      never
    >(FeatureNames.hemsConfigurationOverview);
    this.productionLimitation$ = this.installationDetailsStore.getFeaturePowerLimitationOverview(
      PowerLimitationType.production
    );
    this.powerFlows$ = this.installationFeaturesStore.selectInstallationHemsFeature<
      EmsPowerFlowsProperties,
      never
    >(FeatureNames.emsPowerFlows);
    this.pvStatus$ = this.devicesStore.getPhotovoltaicDevice$.pipe(
      filter((device) => !!device),
      switchMap((device) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature<
          PhotovoltaicStatusProperties,
          never
        >(FeatureNames.photovoltaicStatus, device.id)
      )
    );

    this.devicesStore.isOBNGWallboxPresent$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isOBNGWallboxPresent) => {
        this.isOBNG = isOBNGWallboxPresent;
      });

    this.dryConcatPowerInstantaneousPower$ = this.devicesStore.dryContactHeatPump$.pipe(
      switchMap((device) => {
        if (device) {
          return this.installationFeaturesStore.selectInstallationDeviceFeature<
            IsNumberValueProperty,
            never
          >(FeatureNames.dryContactInstantaneousPower, device.id);
        } else return of(undefined);
      })
    );

    this.emsPowerBalance$ = this.installationFeaturesStore
      .selectInstallationHemsFeature<EmsPowerBalanceProperties, never>(FeatureNames.emsPowerBalance)
      .pipe(map((feature) => this.sortPowerBalanceVcsByBusAddress(feature)));

    this.genericDeviceNames$ = this.devicesStore.dryContactGeneric$.pipe(
      map((dryContactGenerics: RichDevice[]) => dryContactGenerics.map((device) => device.id)),
      switchMap((deviceIds: string[]) => {
        if (deviceIds.length === 0) {
          return of([]);
        }

        const deviceNames$ = deviceIds.map((deviceId: string) =>
          this.installationFeaturesStore
            .selectInstallationDeviceFeature<DeviceNameProperties, DeviceNameCommands>(
              FeatureNames.deviceName,
              deviceId
            )
            .pipe(
              switchMap((feature) =>
                iif(
                  () => !!feature?.properties?.name?.value?.trim()?.length,
                  of({ id: deviceId, name: feature?.properties?.name?.value }),
                  of({
                    id: deviceId,
                    name: `${this.translate.instant(
                      this.otherDeviceFallbackNameTranslationKey
                    )} ${deviceId?.split('-')?.pop()}`,
                  })
                )
              )
            )
        );

        return combineLatest(deviceNames$);
      })
    );

    this.devicesStore.dryContactGeneric$
      .pipe(
        map((dryContactGenerics: RichDevice[]) => dryContactGenerics.map((device) => device.id)),
        switchMap((deviceIds: string[]) => {
          if (deviceIds.length === 0) {
            return of([]);
          }

          return combineLatest(
            deviceIds.map((deviceId) =>
              this.installationFeaturesStore.selectInstallationDeviceFeature<
                IsNumberValueProperty,
                never
              >(FeatureNames.dryContactInstantaneousPower, deviceId)
            )
          ).pipe(
            map((features) => {
              return features.filter((feature) => !!feature) as Feature<
                IsNumberValueProperty,
                never
              >[];
            })
          );
        }),
        combineLatestWith(
          this.dryConcatPowerInstantaneousPower$,
          this.emsPowerBalance$,
          this.genericDeviceNames$
        )
      )
      .subscribe(
        ([dryContactGenericValues, dryContactHeatPump, emsPowerBalance, genericDeviceNames]) => {
          if (dryContactGenericValues.length) {
            this.dryContactgenericDevicePowerDetails = [];
            dryContactGenericValues.forEach(
              (genericConsumptionDevice: Feature<IsNumberValueProperty, never>) => {
                if (genericConsumptionDevice.properties.value.value) {
                  this.dryContactgenericDevicePowerDetails.push({
                    type: EmsPowerBalanceTypes.dryContactGeneric,
                    name: genericDeviceNames.find(
                      (name) => name.id === genericConsumptionDevice.deviceId
                    )?.name,
                    value: genericConsumptionDevice.properties.value.value,
                    unit: genericConsumptionDevice.properties.value.unit,
                  });
                }
              }
            );
          }
          this.powerBalanceTypeToValueMap.clear();
          if (dryContactHeatPump) {
            this.powerBalanceTypeToValueMap.set(EmsPowerBalanceTypes.dryContactHeatpump, [
              {
                type: EmsPowerBalanceTypes.dryContactHeatpump,
                value: dryContactHeatPump.properties.value.value,
                unit: dryContactHeatPump.properties.value.unit,
              },
            ]);
          }
          if (emsPowerBalance) {
            emsPowerBalance.properties?.received.value.forEach((property) => {
              const type = property.type as EmsPowerBalanceTypes;
              // dry contact heat pump already represents the heat pump entry, avoid duplicate row
              if (dryContactHeatPump && type === EmsPowerBalanceTypes.Heatpump) {
                return;
              }

              const items = this.powerBalanceTypeToValueMap.get(type) ?? [];
              items.push({
                type,
                value: property.value,
                unit: property.unit,
                busAddress: property.busAddress,
              });
              this.powerBalanceTypeToValueMap.set(type, items);
            });
          }
        }
      );

    this.setThermalFlowTab();

    // Stream set below in order to prevent .html from overcomplexity
    this.devicesStore.shouldUseConsolidatedFeature$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((shouldUseConsolidatedFeature) => {
        if (shouldUseConsolidatedFeature) {
          this.stateOfChargeConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceType(
            FeatureNames.stateOfChargeConsolidated,
            DeviceTypes.ElectricityStorage
          );
        } else {
          this.stateOfCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.stateOfCapacityNominal
          );
        }
      });
  }

  openHemsLiteHelp(): void {
    this.dialog.open(HemsLiteInfoDialogComponent, {
      width: '560px',
      panelClass: 'hems-lite-info-dialog',
    });
  }

  onSelectFlowType(selectedFlowType: HemsFlowType): void {
    this.isThermalFlow = selectedFlowType === HemsFlowType.Thermal;
  }

  trackClose(): void {
    this.gtmService.track('onClose', 'Energy flows', 'energyFlows');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  // transforming object to feature-like object to support value and unit pipe
  getPropertiesAsFeatureLike<T>(value: EmsPowerBalanceProperty): Feature<T, never> {
    const valueForPipeConsumption: NumberValue = {
      type: DcTypes.number, // EmsPowerBalanceTypes are not supported and number strategy will not pick it
      value: value.value,
      unit: value.unit,
    };

    return (<unknown>{
      properties: { value: valueForPipeConsumption },
    }) as Feature<T, never>;
  }

  private setThermalFlowTab(): void {
    this.emsPowerBalance$
      .pipe(
        concatMap((powerBalance, requestIndex) => {
          if (requestIndex === 0) {
            this.isThermalFlow = !powerBalance;
          }

          return of(powerBalance);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((powerBalance) => {
        this.powerBalance = powerBalance;
        this.cdr.detectChanges();
      });
  }

  private sortPowerBalanceVcsByBusAddress(
    feature: Feature<EmsPowerBalanceProperties, never>
  ): Feature<EmsPowerBalanceProperties, never> {
    const mappedFeature = structuredClone(feature);

    const received = mappedFeature?.properties?.received?.value;

    if (
      received &&
      received.length &&
      received
        .filter((entry) => entry.type === EmsPowerBalanceTypes.VCS)
        .every((entry) => !!entry.busAddress)
    ) {
      mappedFeature.properties.received.value = received.sort((a, b) => {
        if (a.type === EmsPowerBalanceTypes.VCS && b.type === EmsPowerBalanceTypes.VCS) {
          return Number(a.busAddress) - Number(b.busAddress);
        } else {
          return 0;
        }
      });
    }

    return mappedFeature;
  }

  private setWallboxNames(): void {
    this.devicesService.wallboxDevicesWithBusAddress$
      .pipe(
        switchMap((devices) => {
          const mappedDevices = devices.map((device) =>
            this.devicesService
              .getOBNGWallboxDeviceName(device.device)
              .pipe(map((name) => ({ name, device })))
          );

          return combineLatest(mappedDevices);
        }),
        map((data) => data.map((el) => ({ busAddress: el.device.busAddress, name: el.name })))
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data) => {
        this.wallboxNamesWithBusAddress = data;
      });
  }
}
